<template>
  <div
      v-if="note"
      class="editor">
    <div class="editor-header">
      <editor-toolbar
          :editor="editor"
          :buttons="buttons"/>
      <base-button
          ref="menu-button"
          icon="more_vert"
          class="editor-header-menu-button"
          @click="showPopover"/>
      <base-popover
          ref="menu-button-popover"
          :target="$refs['menu-button']">
        <note-menu :buttons="menuButtons"/>
      </base-popover>
    </div>
    <div class="editor-tags">
      <label>
        Tags
      </label>
      <base-token-input
          :key="note.uuid"
          v-model="note.tags"
          type="transparent"
          size="small"
          placeholder="Tags..."
          @tokenAdded="addTag"
          @tokenRemoved="removeTag"/>
    </div>
    <editor-content
        ref="editor"
        class="editor-content"
        :editor="editor"/>
  </div>
  <message-screen
      v-else
      icon="error_outlined"
      message="Note not found"/>
</template>

<script>
import { mapState } from 'vuex';

import { Editor, EditorContent } from 'tiptap';
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  CodeBlock,
  HardBreak,
  Heading,
  History,
  Italic,
  Link,
  OrderedList,
  Strike,
  TodoList,
  Underline,
} from 'tiptap-extensions';

import Image from '@/editor/nodes/Image';
import ListItem from '@/editor/nodes/ListItem';
import TodoItem from '@/editor/nodes/TodoItem';

import BaseButton from '@/components/core/BaseButton.vue';
import BasePopover from '@/components/core/BasePopover.vue';
import BaseTokenInput from '@/components/core/BaseTokenInput.vue';
import MessageModal from '@/components/core/modal/MessageModal.vue';
import MessageScreen from '@/components/core/MessageScreen.vue';

import EditorToolbar from './toolbar/TheToolbar.vue';
import ExportModal from './ExportModal.vue';
import NoteMenu from './NoteMenu.vue';
import UrlModal from './UrlModal.vue';

export default {
  name: 'editor',
  components: {
    BaseButton,
    BasePopover,
    BaseTokenInput,
    EditorContent,
    EditorToolbar,
    MessageScreen,
    NoteMenu,
  },
  computed: {
    uuid() {
      return this.$route.params.id;
    },
    buttons() {
      return [
        [
          this.menuItem('Undo', 'undo', 'undo', () => true, false),
          this.menuItem('Redo', 'redo', 'redo', () => true, false),
        ],
        [
          this.menuItem('Bold', 'format_bold', 'bold'),
          this.menuItem('Italics', 'format_italics', 'italic'),
          this.menuItem('Underline', 'format_underline', 'underline'),
          this.menuItem('Strikethrough', 'strikethrough_s', 'strike'),
        ],
        [
          this.menuItemHeading(1),
          this.menuItemHeading(2),
          this.menuItemHeading(3),
        ],
        [
          this.menuItem('Bullet List', 'format_list_bulleted', 'bullet_list',
            () => !this.editor.isActive.heading()
              && !this.editor.isActive.todo_item()
              && !this.editor.isActive.code_block()),
          this.menuItem('Ordered List', 'format_list_numbered', 'ordered_list',
            () => !this.editor.isActive.heading()
              && !this.editor.isActive.todo_item()
              && !this.editor.isActive.code_block()),
          this.menuItem('Todo List', 'check_box', 'todo_list',
            () => !this.editor.isActive.heading()
              && !this.editor.isActive.list_item()
              && !this.editor.isActive.code_block()),
        ],
        [
          this.menuItem('Quote', 'format_quote', 'blockquote'),
          this.menuItem('Code', 'code', 'code'),
          this.menuItem('Code Block', 'code', 'code_block'),
          {
            text: 'Link',
            icon: 'link',
            enabled: () => {
              const hasSelection = this.editor.selection.to - this.editor.selection.from > 0;
              const validBlocks = !this.editor.isActive.code()
                                  && !this.editor.isActive.code_block();
              return hasSelection && validBlocks;
            },
            command: this.showLinkPrompt,
            active: this.editor.isActive.link(),
          },
        ],
      ];
    },
    menuButtons() {
      return [
        {
          text: this.note?.archived ? 'Unarchive' : 'Archive',
          icon: 'archive',
          func: this.archiveNote,
        },
        {
          text: this.note?.deleted ? 'Restore' : 'Delete',
          icon: this.note?.deleted ? 'restore' : 'delete',
          func: this.deleteNote,
        },
        {
          text: 'Permanently Delete',
          icon: 'delete',
          visible: this.note?.deleted,
          func: this.permanentlyDeleteNote,
        },
        {
          text: 'Export',
          icon: 'get_app',
          func: this.export,
        },
      ];
    },
    ...mapState({
      note: state => state.notes.current,
      storageProviderReady: state => state.storageProviderReady,
    }),
  },
  data() {
    return {
      editor: new Editor({
        autoFocus: true,
        extensions: [
          new Blockquote(),
          new Bold(),
          new BulletList(),
          new Code(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new History(),
          new Image({ store: this.$store }),
          new Italic(),
          new Link(),
          new ListItem(),
          new OrderedList(),
          new Strike(),
          new TodoItem(),
          new TodoList(),
          new Underline(),
        ],
        dropCursor: {
          color: '#fff',
        },
        onUpdate: () => {
          this.onEditorUpdate();
        },
      }),
    };
  },
  created() {
    this.loadData();
  },
  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    loadData() {
      // Don't load data if provider isn't ready
      // Callbacks and watchers will call this method again once it's set
      if (!this.storageProviderReady) {
        return;
      }
      this.$store.dispatch('notes/getNote', {
        uuid: this.uuid,
      });
    },
    onEditorUpdate() {
      this.$store.dispatch('notes/stageNote', {
        content: this.editor.getJSON(),
      });
    },
    menuItem(text, icon, command, enabled = () => true, active = true) {
      return {
        text,
        icon,
        enabled,
        command: this.editor.commands[command],
        active: active ? this.editor.isActive[command]() : undefined,
      };
    },
    menuItemHeading(level) {
      return {
        text: `Heading ${level}`,
        icon: 'title',
        enabled: () => !this.editor.isActive.bullet_list() && !this.editor.isActive.ordered_list(),
        command: this.editor.commands.heading,
        commandArguments: { level },
        active: this.editor.isActive.heading({ level }),
      };
    },
    showLinkPrompt() {
      this.$modal.show({
        component: UrlModal,
        properties: {
          url: this.editor.getMarkAttrs('link').href,
          onSetUrl: url => {
            this.editor.commands.link({ href: url });
            this.$modal.dismiss();
          },
        },
      });
    },
    showPopover(event) {
      this.$refs['menu-button-popover'].toggle(event);
    },
    archiveNote() {
      this.$store.dispatch('notes/setArchived', {
        note: this.note,
        archived: !this.note.archived,
      });
    },
    deleteNote() {
      this.$store.dispatch('notes/setDeleted', {
        note: this.note,
        deleted: !this.note.deleted,
      });
    },
    permanentlyDeleteNote() {
      this.$modal.show({
        component: MessageModal,
        properties: {
          icon: 'delete',
          title: 'Permanently Delete',
          message: 'Are you sure you want to permanently delete this note?',
          buttons: [
            {
              text: 'Cancel',
              func: this.$modal.dismiss,
            },
            {
              text: 'Delete',
              func: () => {
                this.$store.dispatch('notes/deleteNote', {
                  uuid: this.note.uuid,
                });
                this.$modal.dismiss();
                this.$router.push({
                  name: 'notes',
                  query: this.$route.query,
                }).catch(() => {});
              },
            },
          ],
        },
      });
    },
    export() {
      this.$modal.show({
        component: ExportModal,
        properties: {
          note: this.note,
        },
      });
    },
    addTag(tag) {
      this.$store.dispatch('notes/addTag', {
        note: this.note,
        tag,
      });
    },
    removeTag(tag) {
      this.$store.dispatch('notes/removeTag', {
        note: this.note,
        tag,
      });
    },
  },

  watch: {
    $route() {
      this.loadData();
    },
    storageProviderReady() {
      this.loadData();
    },
    note(newNote, oldNote) {
      if (!newNote) {
        return;
      }

      // Remove unused images from db on page unload
      if (oldNote) {
        this.$store.dispatch('notes/deleteUnusedNoteImages', { note: oldNote });
      }
      this.editor.options.content = newNote.content;
      this.editor.view.updateState(this.editor.createState());
      this.editor.view.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';

.editor {
  background: $dark-gray-900;
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  overflow: auto;

  &-header {
    align-items: center;
    background: $dark-gray-900;
    border-bottom: 0.0625rem solid $dark-gray-700;
    display: flex;
    justify-content: space-between;
    min-height: 4rem;
    padding: 0.25rem 1rem;
    position: sticky;
    top: 0;
    width: 100%;
    z-index: 1;

    &-menu-button {
      margin: 0 0.5rem !important;
    }
  }

  &-tags {
    align-items: baseline;
    border-bottom: 0.0625rem solid $dark-gray-700;
    display: flex;
    justify-content: stretch;
    padding: 0.25rem 1.5rem;

    label {
      font-size: 0.875rem;
      margin-right: 0.75rem;
    }

    .token-input {
      flex-grow: 1;
    }
  }

  &-content {
    display: flex;
    flex-grow: 1;
    padding: 1rem;
    position: relative;
    width: 100%;
  }
}
</style>

<style lang="scss">
@import '@/styles/colors.scss';

.ProseMirror {
  caret-color: $text;
  min-height: 100%;
  outline: none;
  padding: 0 1rem 1rem;
  white-space: pre-wrap;
  width: 100%;

  @import '@/styles/prosemirror.scss';
}
</style>
